import Settings from '../domain/Settings';
import pubsub from '../utils/pubsub';
import screenUtil from '../utils/screen';

let breakpoints: Record<string, number>,
	currentBreakpoint: string;

_setEventListeners();

export function init() {
	const settings = Settings.getInstance();

	_validateBreakpoints(settings.breakpoints);

	breakpoints = buildBreakpoints(settings.breakpoints, settings.breakpointSettings);
	currentBreakpoint = calculateCurrentBreakpoint();
}

function _setEventListeners() {
	pubsub.subscribe('resize', function() {
		if (typeof breakpoints === 'undefined') {
			return;
		}

		if (screenUtil.isFullscreen()) {
			return;
		}

		const previousBreakpoint = currentBreakpoint;

		currentBreakpoint = calculateCurrentBreakpoint();

		if (previousBreakpoint !== currentBreakpoint) {
			pubsub.publish('breakpoint.changed', currentBreakpoint);
		}
	});
}

function _validateBreakpoints(bps: Record<string, unknown>): asserts bps is Record<string, number> {
	const values: number[] = [];

	Object.entries(bps).forEach(([bp, value]) => {
		if (typeof value !== 'number' || value < 0) {
			throw new Error(`Invalid breakpoint '${bp}'. '${value}' is not a number or less than zero.`);
		}

		if (values.includes(value)) {
			throw new Error(`Invalid breakpoint '${bp}'. Breakpoint at '${value}' already exists.`);
		}

		values.push(value);
	});
}

function buildBreakpoints(bpConfig: Record<string, number>, settings?: Settings['breakpointSettings']): Record<string, number> {
	const unit = settings?.unit ?? 'px',
		normalizedBreakpoints =
			Object.fromEntries(
				Object.entries(bpConfig)
					.map(([bp, value]) => [bp, normalizeUnitToPixel(value, unit)])
			);

	if (Math.min(...Object.values(normalizedBreakpoints)) > 0) {
		normalizedBreakpoints['0'] = 0;
	}

	return normalizedBreakpoints;
}

function normalizeUnitToPixel(value: number, unit: Settings['breakpointSettings']['unit']): number {
	switch (unit) {
		case 'px':
			return value;
		case 'rem':
			return remToPixel(value);
		default:
			throw new Error(`Unsupported breakpoint unit '${unit}'`);
	}
}

function remToPixel(rem: number): number {
	return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

function calculateCurrentBreakpoint() {
	const screenWidth = screenUtil.getSize().width;

	return Object.keys(breakpoints).reduce((agg, bp) => {
		if (breakpoints[bp] <= screenWidth) {
			if (agg === null || breakpoints[bp] > breakpoints[agg]) {
				agg = bp;
			}
		}

		return agg;
	}, null);
}

export function getCurrentBreakpoint() {
	return currentBreakpoint;
}

export function getBreakpoints() {
	return breakpoints;
}

export default {
	init,
	getCurrentBreakpoint,
	getBreakpoints
};
